import { dontAwait } from "@glideapps/ts-necessities";
import { OwnerKind } from "common/Database/owner-kind";
import { getOrgMembers, getOrgOwner } from "designer/apps/orgs/orgs-lib";
import { ButtonVNext } from "designer/ui/smart/button-vnext/button-vnext";
import { useAppID } from "hooks/use-app-id";
import { useBuilderSelector } from "hooks/use-builder-selector";
import { useBuilderTheme } from "hooks/use-builder-theme";
import { GlideFC } from "lib/glide-fc";
import { useCallback, useEffect, useState } from "react";
import { getAppOwner } from "store/types";
import PaymentInfo, { CardInfo } from "./payment-info";
import { PaymentInfoModalStyle } from "./payment-info-modal-style";
import { cardIsValid, createStripeCard } from "./payment-info-utils";
import { MaybeStripePayButton, PaymentRequestInfo } from "./stripe-pay-button";

function useOwnerEmail(emailSetter: (email: string) => void): void {
    const appID = useAppID();
    const userData = useBuilderSelector(state => state.userData);
    const organizations = useBuilderSelector(state => state.organizations);

    useEffect(() => {
        const fn = async () => {
            if (appID === undefined) {
                return;
            }

            const ownerOrOrg = getAppOwner(appID, userData, organizations);

            if (ownerOrOrg?.kind === OwnerKind.User) {
                emailSetter(ownerOrOrg.email ?? "");
            }

            if (ownerOrOrg?.kind === OwnerKind.Organization) {
                const members = await getOrgMembers(ownerOrOrg.id);
                if (members === undefined) {
                    return;
                }

                const owner = getOrgOwner(members);
                emailSetter(owner?.email ?? "");
            }
        };
        dontAwait(fn());
    }, [appID, emailSetter, organizations, userData]);
}

interface Props {
    padTop: boolean;
    cardError: boolean;
    paymentRequestInfo: PaymentRequestInfo;

    onUpdateCard: (cardInfo: CardInfo, stripeCard: any) => void;
    onCanAcceptChange: (canAccept: boolean) => void;
    onPayButton: (paymentMethod: string) => Promise<boolean>;

    inlineCancel?: boolean;
    onInlineCancel?: () => void;
}

export const PaymentInfoController: GlideFC<Props> = p => {
    const {
        onCanAcceptChange,
        onUpdateCard,
        inlineCancel,
        onInlineCancel,
        padTop,
        cardError,
        onPayButton,
        paymentRequestInfo,
    } = p;

    const [stripeCard, setStripeCard] = useState<any>();
    const [stripeCardReady, setStripeCardReady] = useState(false);
    const [cardFocused, setCardFocused] = useState(false);

    const theme = useBuilderTheme();

    const [cardInfo, setCardInfo] = useState<CardInfo>({
        nameOnCard: "",
        receiptEmail: "",
        add1: "",
        add2: "",
        city: "",
        state: "",
        zip: "",
        country: "Country",
    });

    useOwnerEmail(
        useCallback(email => {
            setCardInfo(info => ({
                ...info,
                receiptEmail: info.receiptEmail === "" ? email : info.receiptEmail,
            }));
        }, [])
    );

    const onCancel = useCallback(() => {
        if (onInlineCancel === undefined) return;
        onInlineCancel();
    }, [onInlineCancel]);

    const onCardFocus = useCallback(() => {
        setCardFocused(true);
    }, []);

    const onCardBlur = useCallback(() => {
        setCardFocused(false);
    }, []);

    // This is called on every update to cardInfo, stripeCard, and stripeCardReady
    useEffect(() => {
        const canUpdate = cardIsValid(cardInfo, stripeCard, stripeCardReady);
        onCanAcceptChange(canUpdate);

        if (!canUpdate) return;

        onUpdateCard(cardInfo, stripeCard);
    }, [cardInfo, stripeCard, stripeCardReady, onUpdateCard, onCanAcceptChange]);

    useEffect(() => {
        async function getStripeCard() {
            const sc = await createStripeCard(theme);
            sc.on("change", (event: any) => {
                if (event.complete) {
                    setStripeCardReady(true);
                } else {
                    setStripeCardReady(false);
                }
            });
            sc.on("focus", onCardFocus);
            sc.on("blur", onCardBlur);
            sc.mount("#builderCardWrapper");
            setStripeCard(sc);
            setStripeCardReady(false);
        }

        // Prevent update loop, only initialize card if it is not initialized
        if (stripeCard === undefined) {
            dontAwait(getStripeCard());
        }
    }, [onCardBlur, onCardFocus, stripeCard, theme]);

    return (
        <PaymentInfoModalStyle padTop={padTop} cardFocused={cardFocused}>
            <div className="title-with-cancel">
                <div className="payment-info-label">Please enter your payment information</div>
                {inlineCancel && (
                    <ButtonVNext buttonType="minimal" variant="accent" size="sm" onClick={onCancel} label="Cancel" />
                )}
            </div>
            {cardError && (
                <div className="error-message">
                    <div className="error-title">Something went wrong updating your payment information.</div>
                    <div className="error-msg">
                        Please{" "}
                        <a
                            href="https://glide-help.zendesk.com/hc/en-us/requests/new?ticket_form_id=1500000517961"
                            target="_blank"
                            rel="noopener noreferrer">
                            contact support
                        </a>
                    </div>
                </div>
            )}
            <MaybeStripePayButton onPay={onPayButton} paymentRequestInfo={paymentRequestInfo} />
            <div className="cardWrapper">
                <div id="builderCardWrapper" />
            </div>
            <PaymentInfo cardInfo={cardInfo} onCardInfoUpdate={setCardInfo} />
        </PaymentInfoModalStyle>
    );
};
